/**
 * @file handles the generic AG Grid Column Defnitions for allocation header in all allocation specific tables.
 */

import { months } from "@src/constants";
import { ColDef, ColGroupDef, ValueFormatterParams } from "ag-grid-community";
import dayjs, { Dayjs } from "dayjs";

let startDateDayjs: Dayjs;
let endDateDayjs: Dayjs;
let getColDefMonth: (year: number, month: number) => ColDef;

/**
 * function for setting up the column definitions for the allocations.
 *
 * Allocation headers contain years (as ColGroupDef) and months (as ColDef; children of years).
 * They always have numeric values. The default valueFormatter will hide the number 0 in the cell and show them in percent (e.g. "100%").
 *
 * This function takes default values for the "month" colDefs.
 * You can provide an own Column Definition for the "month" colDefs. Just parse in a function that expects year and month as numbers and builds the definition regarding these two values.
 * (month indexes are in range 1 to 12).
 * The provided one will override the default values.
 *
 * @param {Date} startDate first date that needs to be considered for the table
 * @param {Date} endDate last date that needs to be considered for the table
 * @param {Function} getColDefMonthParam function for building the "month" colDef. gets the year and month as parameters to build a column definition for this date.
 *
 * @returns {ColGroupDef[]} returns an array of ColGroupDefs for the allocations table
 */
export function getAllocationColumnDefs(
  startDate: Date,
  endDate: Date,
  getColDefMonthParam?: (year: number, month: number) => ColDef
): ColGroupDef[] {
  if (!endDate || !startDate) return [];

  startDateDayjs = dayjs(startDate);
  endDateDayjs = dayjs(endDate);
  getColDefMonth = getColDefMonthParam;

  return getYearsAndMonthsColDefs();
}

/**
 * helper method for looping through the years to get all colGroupDefs.
 * every year is a colGroupDef containing colDefs of months.
 *
 * @returns {ColGroupDef[]} all yearly colGroupDefs
 */
function getYearsAndMonthsColDefs(): ColGroupDef[] {
  const resultColDefs: ColGroupDef[] = [];

  // loop through all years until project end date
  for (let i = startDateDayjs.year(); i <= endDateDayjs.year(); i++) {
    const colDef: ColGroupDef = getColGroupDefYear(i);
    resultColDefs.push(colDef);
  }

  return resultColDefs;
}

/**
 * builds the colGroupDef for the specific year
 *
 * @param {number} year year to be considered in the colGroupDef
 * @returns {ColGroupDef} colGroupDef for the year
 */
function getColGroupDefYear(year: number): ColGroupDef {
  const monthlyColDefs: ColDef[] = getMonthlyColDefs(year);

  return {
    groupId: "allocation",
    headerName: year.toString(),
    headerClass: "year-header-cell",
    children: monthlyColDefs,
  };
}

/**
 * helper method for looping through the months to get all colDefs
 *
 * @param {number} year year to be considered in the colDef
 * @returns {ColDef[]} all monthly colDefs
 */
function getMonthlyColDefs(year: number): ColDef[] {
  const monthlyColDefs: ColDef[] = [];

  const firstMonthIndex: number =
    year === startDateDayjs.year() ? startDateDayjs.month() + 1 : 1;

  const lastMonthIndex: number =
    year === endDateDayjs.year() ? endDateDayjs.month() + 1 : 12;

  // loop through all months of the year
  for (let i = firstMonthIndex; i <= lastMonthIndex; i++) {
    const defaultColDef = getDefaultColDefMonth(year, i);
    const customMonthColDef = getColDefMonth?.(year, i);

    const colDef = customMonthColDef
      ? { ...defaultColDef, ...customMonthColDef }
      : defaultColDef;

    monthlyColDefs.push(colDef);
  }

  return monthlyColDefs;
}

function getDefaultColDefMonth(year: number, monthIndex: number): ColDef {
  return {
    colId: `${monthIndex}-${year}`,
    headerName: months[monthIndex - 1],
    minWidth: 64,
    maxWidth: 64,
    headerClass: "month-header-cell",
    sortable: false,
    editable: false,
    cellClass: "allocation-cell",
    resizable: false,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? `${params.value}%` : "";
    },
  };
}
